/**
 * ModWatch Cart Persistence & Universal Add-To-Cart Engine (v2)
 * 1. Guarantees Cart Persistence across sessions, re-entry, and browser restarts.
 * 2. Properly handles REMOVAL / DELETION: When a customer deletes an item or clears their cart,
 *    it is immediately erased from both memory (LocalStorage) and the server cart, so deleted items never return.
 * 3. Ensures all Add-To-Cart buttons work universally across Home, Products, Collections, Recommendations & PDP.
 * 4. Real-time Cart Counter synchronization across headers, drawers, and floating badges.
 */

(function() {
  'use strict';

  const STORAGE_KEY = 'mw_persistent_cart_v2';
  const TIMESTAMP_KEY = 'mw_cart_last_sync_v2';
  const USER_CLEARED_FLAG = 'mw_user_explicitly_cleared_cart';
  const MAX_STORAGE_DAYS = 30; // Persist cart items for up to 30 days

  // Global Cart State Manager
  window.ModWatchCart = {
    // 1. Save current cart state to LocalStorage
    saveCart: function(cart) {
      if (!cart) return;

      if (!cart.items || cart.items.length === 0 || cart.item_count === 0) {
        this.clearSavedCart();
        return;
      }

      try {
        sessionStorage.removeItem(USER_CLEARED_FLAG);
        const payload = {
          items: cart.items.map(item => ({
            id: item.id,
            key: item.key,
            variant_id: item.variant_id || item.id,
            quantity: item.quantity,
            title: item.title,
            price: item.price,
            image: item.image,
            url: item.url,
            properties: item.properties || {}
          })),
          item_count: cart.item_count,
          total_price: cart.total_price,
          timestamp: Date.now()
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        localStorage.setItem(TIMESTAMP_KEY, Date.now().toString());
        this.updateCartBadges(cart.item_count);
      } catch (e) {
        console.warn('[ModWatch Cart] Storage save error:', e);
      }
    },

    // 2. Clear saved cart completely (Memory + LocalStorage)
    clearSavedCart: function() {
      try {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(TIMESTAMP_KEY);
        sessionStorage.setItem(USER_CLEARED_FLAG, 'true');
        this.updateCartBadges(0);
      } catch (e) {}
    },

    // 3. Remove single item from memory
    removeSavedItem: function(variantIdOrKey) {
      try {
        const saved = this.getSavedCart();
        if (!saved || !saved.items) return;

        const updatedItems = saved.items.filter(item => {
          const matchId = (item.variant_id == variantIdOrKey || item.id == variantIdOrKey);
          const matchKey = (item.key && item.key == variantIdOrKey);
          return !matchId && !matchKey;
        });

        if (updatedItems.length === 0) {
          this.clearSavedCart();
        } else {
          saved.items = updatedItems;
          saved.item_count = updatedItems.reduce((acc, curr) => acc + (curr.quantity || 1), 0);
          saved.timestamp = Date.now();
          localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
          this.updateCartBadges(saved.item_count);
        }
      } catch (e) {}
    },

    // 4. Retrieve saved cart from LocalStorage
    getSavedCart: function() {
      try {
        // If user recently clicked delete all / empty cart in this browser session, do not restore
        if (sessionStorage.getItem(USER_CLEARED_FLAG) === 'true') {
          return null;
        }

        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        const ageInDays = (Date.now() - (data.timestamp || 0)) / (1000 * 60 * 60 * 24);
        if (ageInDays > MAX_STORAGE_DAYS) {
          this.clearSavedCart();
          return null;
        }
        return data;
      } catch (e) {
        return null;
      }
    },

    // 5. Update Cart Badges across Header and Drawers
    updateCartBadges: function(count) {
      const parsedCount = parseInt(count) || 0;
      
      document.querySelectorAll('.cart-count-bubble, #cart-icon-bubble .cart-count, .mw-cart-count-badge, .header__icon--cart .cart-count-bubble').forEach(el => {
        el.textContent = parsedCount;
        if (parsedCount > 0) {
          el.style.display = '';
          el.classList.remove('hidden');
        } else {
          el.classList.add('hidden');
        }
      });

      document.querySelectorAll('#cart-icon-bubble span[aria-hidden="true"]').forEach(el => {
        el.textContent = parsedCount;
      });
    },

    // 6. Hydrate / Re-sync Cart on Page Load
    initCartSync: function() {
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(res => res.json())
        .then(remoteCart => {
          if (remoteCart && remoteCart.item_count > 0) {
            // Remote cart has items -> update memory
            this.saveCart(remoteCart);
          } else {
            // Remote cart is empty -> check if user had previous items to restore
            const saved = this.getSavedCart();
            if (saved && saved.items && saved.items.length > 0) {
              this.restoreSavedCart(saved.items);
            } else {
              this.updateCartBadges(0);
            }
          }
        })
        .catch(err => {
          console.warn('[ModWatch Cart] Cart sync fetch error:', err);
        });
    },

    // 7. Restore Saved Cart Items to Shopify Session
    restoreSavedCart: function(items) {
      if (!items || items.length === 0) return;

      const itemsToAdd = items.map(i => ({
        id: i.variant_id || i.id,
        quantity: i.quantity || 1,
        properties: i.properties || {}
      }));

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: itemsToAdd })
      })
      .then(r => r.json())
      .then(() => fetch('/cart.js').then(r => r.json()))
      .then(finalCart => {
        this.saveCart(finalCart);
      })
      .catch(err => {
        console.warn('[ModWatch Cart] Auto-restore error:', err);
      });
    },

    // 8. Universal Add To Cart Helper (Without Cart Page Redirection)
    addItem: function(variantId, quantity, options) {
      options = options || {};
      const qty = parseInt(quantity) || 1;
      if (!variantId) return Promise.reject(new Error('Missing variant ID'));

      sessionStorage.removeItem(USER_CLEARED_FLAG);

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [{
            id: variantId,
            quantity: qty,
            properties: options.properties || {}
          }]
        })
      })
      .then(res => {
        if (!res.ok) throw new Error('Add to cart failed');
        return res.json();
      })
      .then(() => fetch('/cart.js?_t=' + Date.now(), { cache: 'no-store' }).then(r => r.json()))
      .then(updatedCart => {
        this.saveCart(updatedCart);
        document.dispatchEvent(new CustomEvent('modwatch:cart:updated', { detail: updatedCart }));

        if (options.targetBtn) {
          window.ModWatchCart.animateButtonSuccess(options.targetBtn);
        }
        window.ModWatchCart.showToastNotification('✓ Watch added to your cart');

        if (options.redirectToCart === true) {
          window.location.href = '/cart';
        }
        return updatedCart;
      });
    },

    // 9. Button Success Animation
    animateButtonSuccess: function(btn) {
      if (!btn) return;
      const origContent = btn.dataset.origHtml || btn.innerHTML;
      const origBg = btn.style.backgroundColor;
      const origColor = btn.style.color;
      const origBorder = btn.style.borderColor;

      btn.classList.remove('mw-is-adding', 'loading');
      btn.classList.add('mw-is-added-success');
      btn.disabled = false;
      btn.style.transition = 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)';
      btn.style.backgroundColor = '#10B981';
      btn.style.borderColor = '#10B981';
      btn.style.color = '#FFFFFF';
      btn.style.transform = 'scale(1.02)';

      btn.innerHTML = `
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#FFFFFF" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="display:inline-block; vertical-align:middle; margin-right:6px;">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
        <span style="color:#FFFFFF !important; font-weight:700;">ADDED TO CART</span>
      `;

      setTimeout(() => {
        btn.style.transform = 'scale(1)';
      }, 250);

      setTimeout(() => {
        btn.style.backgroundColor = origBg;
        btn.style.borderColor = origBorder;
        btn.style.color = origColor;
        btn.innerHTML = origContent;
        btn.classList.remove('mw-is-added-success');
      }, 2400);
    },

    // 10. Luxury Floating Toast Notification
    showToastNotification: function(msg) {
      let toast = document.getElementById('MwCartToast');
      if (!toast) {
        toast = document.createElement('div');
        toast.id = 'MwCartToast';
        toast.style.cssText = `
          position: fixed;
          bottom: 24px;
          right: 24px;
          background: #030816;
          color: #FFFFFF;
          border: 1px solid rgba(197, 168, 128, 0.4);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.25);
          border-radius: 12px;
          padding: 14px 20px;
          display: flex;
          align-items: center;
          gap: 14px;
          z-index: 999999;
          font-family: 'Outfit', sans-serif;
          font-size: 13.5px;
          font-weight: 600;
          opacity: 0;
          transform: translateY(20px);
          transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
          pointer-events: auto;
        `;
        document.body.appendChild(toast);
      }

      toast.innerHTML = `
        <div style="display: flex; align-items: center; gap: 8px;">
          <div style="width: 20px; height: 20px; border-radius: 50%; background: #10B981; display: flex; align-items: center; justify-content: center;">
            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="#FFFFFF" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <span>${msg}</span>
        </div>
        <a href="/cart" style="color: #C5A880; font-weight: 700; text-decoration: underline; font-size: 13px; margin-left: 8px;">
          View Cart &rarr;
        </a>
      `;

      requestAnimationFrame(() => {
        toast.style.opacity = '1';
        toast.style.transform = 'translateY(0)';
      });

      if (window._mwToastTimer) clearTimeout(window._mwToastTimer);
      window._mwToastTimer = setTimeout(() => {
        toast.style.opacity = '0';
        toast.style.transform = 'translateY(20px)';
      }, 3200);
    }
  };

  // -------------------------------------------------------------
  // Universal DOM Event Delegation for ALL Add-To-Cart & Remove Actions
  // -------------------------------------------------------------
  document.addEventListener('DOMContentLoaded', function() {
    // Inject Spinner Stylesheet
    if (!document.getElementById('mwSpinStyles')) {
      const styleEl = document.createElement('style');
      styleEl.id = 'mwSpinStyles';
      styleEl.textContent = `
        @keyframes mwSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .mw-spin-svg {
          display: inline-block !important;
          vertical-align: middle !important;
          margin-right: 8px !important;
          animation: mwSpin 0.75s linear infinite !important;
          transform-origin: center center !important;
          flex-shrink: 0 !important;
        }
      `;
      document.head.appendChild(styleEl);
    }

    // 1. Run persistence sync immediately
    window.ModWatchCart.initCartSync();

    // 2. Global Click Delegation for any Add to Cart Button across whole site
    document.addEventListener('click', function(e) {
      const targetBtn = e.target.closest('[data-action="add-to-cart"], .mw-shop-atc-btn, .mw-pdp-atc-btn, .mw-related-atc-btn, .mw-quick-add-btn, [name="add"]:not(.disabled)');
      if (!targetBtn) return;

      if (targetBtn.classList.contains('mw-is-adding') || targetBtn.classList.contains('mw-is-added-success') || targetBtn.disabled) return;

      const form = targetBtn.closest('form');
      let variantId = targetBtn.dataset.variantId || targetBtn.getAttribute('data-variant-id');
      let qty = 1;

      if (!variantId && form) {
        const idInput = form.querySelector('[name="id"], .product-variant-id');
        if (idInput && idInput.value) {
          variantId = idInput.value;
        }
        const qtyInput = form.querySelector('[name="quantity"]');
        if (qtyInput && qtyInput.value) {
          qty = parseInt(qtyInput.value) || 1;
        }
      }

      if (variantId) {
        e.preventDefault();
        e.stopPropagation();

        targetBtn.dataset.origHtml = targetBtn.innerHTML;
        targetBtn.classList.add('mw-is-adding');
        targetBtn.innerHTML = `
          <svg class="mw-spin-svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5">
            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.5" opacity="0.25"></circle>
            <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"></path>
          </svg>
          <span style="vertical-align: middle;">ADDING...</span>
        `;
        targetBtn.disabled = true;

        window.ModWatchCart.addItem(variantId, qty, { redirectToCart: false, targetBtn: targetBtn })
          .catch(() => {
            targetBtn.innerHTML = targetBtn.dataset.origHtml || 'ADD TO CART';
            targetBtn.disabled = false;
            targetBtn.classList.remove('mw-is-adding');
          });
      }
    }, true);

    // 3. Global Form Submit Delegation for any form with action="/cart/add"
    document.addEventListener('submit', function(e) {
      const form = e.target;
      if (!form || !form.action || !form.action.includes('/cart/add')) return;

      const variantInput = form.querySelector('[name="id"]');
      if (variantInput && variantInput.value) {
        e.preventDefault();
        const qtyInput = form.querySelector('[name="quantity"]');
        const qty = qtyInput ? (parseInt(qtyInput.value) || 1) : 1;
        const submitBtn = form.querySelector('[type="submit"], button[name="add"], .mw-pdp-atc-btn');

        if (submitBtn) {
          submitBtn.dataset.origHtml = submitBtn.innerHTML;
          submitBtn.disabled = true;
          submitBtn.classList.add('mw-is-adding');
          submitBtn.innerHTML = `
            <svg class="mw-spin-svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5">
              <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.5" opacity="0.25"></circle>
              <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"></path>
            </svg>
            <span style="vertical-align: middle;">ADDING...</span>
          `;
        }

        window.ModWatchCart.addItem(variantInput.value, qty, { redirectToCart: false, targetBtn: submitBtn })
          .catch(() => {
            if (submitBtn) {
              submitBtn.innerHTML = submitBtn.dataset.origHtml || 'ADD TO CART';
              submitBtn.disabled = false;
              submitBtn.classList.remove('mw-is-adding');
            }
          });
      }
    });

  });

  // Listen for storage changes across tabs/windows
  window.addEventListener('storage', function(e) {
    if (e.key === STORAGE_KEY) {
      if (!e.newValue) {
        window.ModWatchCart.updateCartBadges(0);
      } else {
        try {
          const data = JSON.parse(e.newValue);
          if (data && data.item_count !== undefined) {
            window.ModWatchCart.updateCartBadges(data.item_count);
          }
        } catch (err) {}
      }
    }
  });

})();
